/**
 * People and roles (ADR-034): who holds which of the app's roles here, the
 * roles themselves (with any new permissions waiting for an owner to accept),
 * grants, and sensitive-role proposals waiting for a second person.
 *
 * The screen offers only what the server would allow, but decides nothing:
 * every write re-checks the seat, the role's sensitivity and the person's
 * membership under the app's lock. A sensitive role is proposed with a
 * step-up proof and approved by a different access manager with another; the
 * server says when no second person exists and grants it alone, on the
 * record.
 */
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import {
  APP_ACCESS_BULK_GRANT_MAX,
  type AppAccessScreenDto,
  type VaultHolderMemberDto,
} from '@/lib/api';
import { StepUpDialog, type StepUpCredentials } from '../../components/StepUpDialog';
import {
  acceptSensitiveAppRoleAdditions,
  approveAppRoleProposal,
  BulkGrantStoppedError,
  isAccessPolicyChanged,
  proposeSensitiveAppRole,
  useAcceptAppRoleAdditions,
  useAppAccessInvalidator,
  useCloseAppRoleProposal,
  useGrantAppRole,
  useGrantAppRoleToMany,
  useRevokeAppRole,
  type AppAccessScope,
} from '../../hooks/useAppAccess';
import { AppText } from './AttributedText';
import { ExplainerPanel } from './ExplainerPanel';
import { SetupRolesPanel } from './SetupRolesPanel';
import { AudiencesCard, FinishSetupCard, RedisclosureCard } from './AudiencesSection';
import { MemberFilter, MemberPager, useMemberPage } from './members';
import {
  appAccessErrorCode,
  describeAppAccessError,
  formatDate,
  memberLabel,
  permissionLabel,
  roleLabel,
  tierMeets,
} from './shared';

interface PeopleSectionProps {
  scope: AppAccessScope;
  appName: string;
  screen: AppAccessScreenDto;
  members: readonly VaultHolderMemberDto[];
  /** Pre-fill the grant form (from an access request). */
  grantFor?: { principalId: string; roleId: string | null } | null;
}

type StepUpAction = {
  title: string;
  description: string;
  confirmLabel: string;
  run: (creds: StepUpCredentials) => Promise<void>;
};

export function PeopleSection({ scope, appName, screen, members, grantFor }: PeopleSectionProps) {
  const { access, proposals, seat } = screen;
  // Every grant and acceptance names the roles this screen shows; a publish
  // since refuses it (ACCESS_POLICY_CHANGED) and the screen reloads.
  const policyId = access.policyId;
  // The approval was revoked (roles remain, reaching nothing): only removing
  // roles and rejecting proposals stay open until it is approved again.
  const admitted = screen.admitted !== false;
  const isOwner = seat.tier === 'owner';
  // Sensitive roles are managed only by owners when the workspace says so —
  // the server's rule, so nobody steps up just to be refused.
  const canApprove = seat.manager && (!screen.settings.ownerOnlySensitive || isOwner);
  const byPrincipal = new Map(members.map((m) => [m.principalId, m]));
  const name = (principalId: string) =>
    principalId === seat.principalId
      ? 'You'
      : memberLabel(byPrincipal.get(principalId), principalId);
  const [stepUp, setStepUp] = useState<StepUpAction | null>(null);
  const [explaining, setExplaining] = useState<string | null>(null);
  const invalidate = useAppAccessInvalidator(scope);
  const revoke = useRevokeAppRole(scope);
  const closeProposal = useCloseAppRoleProposal(scope);
  const accept = useAcceptAppRoleAdditions(scope);
  // Who holds which role, a page at a time: a large workspace never renders
  // every member's assignments at once.
  const activeMembers = useMemo(() => members.filter((m) => m.status === 'active'), [members]);
  const rosterPage = useMemberPage(activeMembers);

  if (!access.activated) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Turn on roles</CardTitle>
          <CardDescription>
            {appName} declares roles, but they are not on in this workspace yet. Until they are, any
            of its data created for roles stays closed (“needs setup”), and its older data follows
            workspace roles.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!admitted ? (
            <p className="text-sm text-muted-foreground">
              Roles can be turned on once {appName} is approved in this workspace again.
            </p>
          ) : isOwner ? (
            <SetupRolesPanel
              scope={scope}
              appName={appName}
              access={access}
              members={members}
              records={screen.records ?? []}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              An owner of this workspace turns roles on, choosing who starts with which role.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  const assignmentsBy = new Map<string, typeof access.assignments>();
  for (const a of access.assignments) {
    assignmentsBy.set(a.principalId, [...(assignmentsBy.get(a.principalId) ?? []), a]);
  }

  return (
    <div className="space-y-6">
      {stepUp && (
        <StepUpDialog
          open
          onOpenChange={(o) => !o && setStepUp(null)}
          title={stepUp.title}
          description={stepUp.description}
          confirmLabel={stepUp.confirmLabel}
          onConfirm={async (creds) => {
            try {
              await stepUp.run(creds);
            } catch (err) {
              // The roles changed under the screen: reload it, and say so in
              // words; someone else changing access now: say that (a step-up
              // write is not retried — the person confirms again).
              if (isAccessPolicyChanged(err)) {
                void invalidate();
                throw new Error(describeAppAccessError(err));
              }
              if (appAccessErrorCode(err) === 'ACCESS_BUSY') {
                throw new Error(describeAppAccessError(err));
              }
              throw err;
            }
          }}
          onSuccess={() => {
            setStepUp(null);
            void invalidate();
          }}
        />
      )}

      {admitted && access.activation?.mode === 'activating' && (
        <FinishSetupCard
          scope={scope}
          appName={appName}
          access={access}
          records={screen.records ?? []}
          isOwner={isOwner}
        />
      )}
      {admitted && (
        <RedisclosureCard
          scope={scope}
          appName={appName}
          access={access}
          records={screen.records ?? []}
          isOwner={isOwner}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Roles</CardTitle>
          <CardDescription>
            Defined by {appName}. Names and descriptions are the app’s own words. Your workspace
            decides who holds them.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {access.roles.map((r) => (
            <div key={r.roleId} className="rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">
                  <AppText appName={appName}>{r.label}</AppText>
                </span>
                {r.sensitive && <Badge variant="destructive">Sensitive</Badge>}
                {r.minTier !== 'viewer' && (
                  <Badge variant="outline" className="capitalize">
                    {r.minTier}+ only
                  </Badge>
                )}
              </div>
              {r.description && (
                <p className="text-muted-foreground">
                  <AppText appName={appName}>{r.description}</AppText>
                </p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Can:{' '}
                {r.accepted.map((p) => permissionLabel(access.permissions, p)).join(', ') ||
                  'nothing yet'}
              </p>
              {r.pendingAdditions.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-amber-50 p-2 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                  <div className="space-y-1">
                    <p>
                      A new release adds:{' '}
                      {r.pendingAdditions.map((p, i) => (
                        <span key={p}>
                          {i > 0 && ', '}
                          {permissionLabel(access.permissions, p)}
                          {r.pendingSensitive.includes(p) && (
                            <Badge variant="destructive" className="ml-1">
                              Sensitive
                            </Badge>
                          )}
                        </span>
                      ))}
                      . Holders don’t get it until an owner accepts.
                    </p>
                    {r.holders !== undefined && (
                      <p data-testid={`holders-${r.roleId}`}>
                        {holdersLine(
                          r.holders,
                          access.assignments
                            .filter((a) => a.roleId === r.roleId)
                            .map((a) => name(a.principalId)),
                        )}
                      </p>
                    )}
                  </div>
                  {isOwner && admitted && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={accept.isPending}
                      onClick={() => {
                        if (r.sensitive) {
                          setStepUp({
                            title: `Accept new permissions for ${r.label}`,
                            description:
                              r.holders === undefined
                                ? 'This role is sensitive. Confirm it’s you to let its holders do more.'
                                : `This role is sensitive. Confirm it’s you to give ${peopleCount(r.holders)} what it adds.`,
                            confirmLabel: 'Accept',
                            run: async (creds) => {
                              await acceptSensitiveAppRoleAdditions(
                                scope,
                                r.roleId,
                                creds,
                                policyId,
                              );
                            },
                          });
                        } else {
                          accept.mutate(
                            { roleId: r.roleId, expectedAccessPolicyId: policyId },
                            {
                              onError: (err) =>
                                toast({
                                  title: 'Not accepted',
                                  description: describeAppAccessError(err),
                                  variant: 'destructive',
                                }),
                            },
                          );
                        }
                      }}
                    >
                      Accept
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <AudiencesCard appName={appName} access={access} records={screen.records ?? []} />

      {seat.manager && admitted && (
        <GrantCard
          // A request's "Give a role…" pre-fills a fresh form.
          key={grantFor ? `${grantFor.principalId}:${grantFor.roleId ?? ''}` : 'grant'}
          scope={scope}
          appName={appName}
          screen={screen}
          members={members}
          initial={grantFor ?? null}
          onSensitive={(body, label) =>
            setStepUp({
              title: `Propose ${label}`,
              description:
                isOwner && !screen.settings.ownerDualControl
                  ? 'This role is sensitive. As an owner you give it alone; it is recorded as granted by one person and the other owners are told.'
                  : isOwner
                    ? 'This role is sensitive. In this workspace owners need a second person too — unless nobody else can approve, when it is granted now and recorded as such.'
                    : 'This role is sensitive. A second access manager — or an owner — approves it before it takes effect.',
              confirmLabel: isOwner && !screen.settings.ownerDualControl ? 'Give role' : 'Propose',
              run: async (creds) => {
                const result = await proposeSensitiveAppRole(scope, body, creds, policyId);
                toast({
                  title:
                    result.status === 'proposed'
                      ? 'Proposed — waiting for a second person'
                      : 'Granted by you alone',
                  description:
                    result.status === 'proposed'
                      ? 'Another access manager or an owner needs to approve it.'
                      : result.reason === 'owner'
                        ? 'As an owner you may. The workspace’s other owners have been told.'
                        : 'No one else can approve it here. The workspace’s other owners have been told.',
                });
              },
            })
          }
        />
      )}

      {proposals.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Waiting for a second person</CardTitle>
            <CardDescription>
              A sensitive role proposed by an admin or appointed manager needs a second person —
              never its proposer, never the person receiving it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {proposals.map((p) => {
              const mine = p.proposedByPrincipalId === seat.principalId;
              const forMe = p.principalId === seat.principalId;
              return (
                <div
                  key={p.proposalId}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2"
                >
                  <span>
                    <span className="font-medium">{roleLabel(access.roles, p.roleId)}</span> for{' '}
                    {name(p.principalId)} — proposed by {name(p.proposedByPrincipalId)}, lapses{' '}
                    {formatDate(p.lapsesAt)}
                  </span>
                  <span className="flex gap-2">
                    {canApprove && admitted && !mine && !forMe && (
                      <Button
                        size="sm"
                        onClick={() =>
                          setStepUp({
                            title: `Approve ${roleLabel(access.roles, p.roleId)}`,
                            description: `You are the second person approving this sensitive role for ${name(p.principalId)}.`,
                            confirmLabel: 'Approve',
                            run: async (creds) => {
                              await approveAppRoleProposal(scope, p.proposalId, creds, policyId);
                            },
                          })
                        }
                      >
                        Approve
                      </Button>
                    )}
                    {(mine || seat.manager) && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={closeProposal.isPending}
                        onClick={() =>
                          closeProposal.mutate(p.proposalId, {
                            onError: (err) =>
                              toast({
                                title: mine ? 'Not withdrawn' : 'Not rejected',
                                description: describeAppAccessError(err),
                                variant: 'destructive',
                              }),
                          })
                        }
                      >
                        {mine ? 'Withdraw' : 'Reject'}
                      </Button>
                    )}
                  </span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Who has which role</CardTitle>
          <CardDescription>
            Removing a role takes effect on the person’s next request. They are told in Fias.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <MemberFilter page={rosterPage} id="roster-find" label="Find a person in the list" />
          {rosterPage.rows.map((m) => {
            const held = assignmentsBy.get(m.principalId) ?? [];
            return (
              <div key={m.principalId} className="rounded-md border border-border p-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <span className="font-medium">{name(m.principalId)}</span>{' '}
                    <span className="text-xs capitalize text-muted-foreground">{m.role}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setExplaining(explaining === m.principalId ? null : m.principalId)
                    }
                  >
                    {explaining === m.principalId ? 'Hide' : 'What can they do?'}
                  </Button>
                </div>
                <div className="mt-1 flex flex-wrap gap-2">
                  {held.length === 0 && <span className="text-muted-foreground">No roles</span>}
                  {held.map((a) => {
                    const role = access.roles.find((r) => r.roleId === a.roleId);
                    const ineligible = role && !tierMeets(m.role, role.minTier);
                    return (
                      <span
                        key={a.assignmentId}
                        className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5"
                      >
                        {role?.label ?? a.roleId}
                        {a.expiresAt && (
                          <span className="text-xs text-muted-foreground">
                            until {formatDate(a.expiresAt)}
                          </span>
                        )}
                        {ineligible && (
                          <span
                            className="text-xs text-amber-700 dark:text-amber-300"
                            title={`Gives nothing while their workspace role is below ${role.minTier}`}
                          >
                            (inactive)
                          </span>
                        )}
                        {(seat.manager || m.principalId === seat.principalId) && (
                          <button
                            type="button"
                            className="ml-1 text-muted-foreground hover:text-destructive"
                            aria-label={`Remove ${role?.label ?? a.roleId} from ${name(m.principalId)}`}
                            disabled={revoke.isPending}
                            onClick={() =>
                              revoke.mutate(a.assignmentId, {
                                onError: (err) =>
                                  toast({
                                    title: 'Not removed',
                                    description: describeAppAccessError(err),
                                    variant: 'destructive',
                                  }),
                              })
                            }
                          >
                            ×
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
                {explaining === m.principalId && (
                  <div className="mt-2">
                    <ExplainerPanel
                      scope={scope}
                      principalId={m.principalId}
                      who={m.principalId === seat.principalId ? 'you' : name(m.principalId)}
                      appName={appName}
                      roles={access.roles}
                      permissions={access.permissions}
                      audiences={access.audiences}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <MemberPager page={rosterPage} label="Pages of the list" />
        </CardContent>
      </Card>
    </div>
  );
}

function GrantCard({
  scope,
  appName,
  screen,
  members,
  initial,
  onSensitive,
}: {
  scope: AppAccessScope;
  appName: string;
  screen: AppAccessScreenDto;
  members: readonly VaultHolderMemberDto[];
  initial: { principalId: string; roleId: string | null } | null;
  onSensitive: (
    body: { principalId: string; roleId: string; expiresAt?: string },
    roleLabel: string,
  ) => void;
}) {
  const grant = useGrantAppRole(scope);
  const grantMany = useGrantAppRoleToMany(scope);
  const [chosen, setChosen] = useState<string[]>(initial ? [initial.principalId] : []);
  const [roleId, setRoleId] = useState(initial?.roleId ?? '');
  const [until, setUntil] = useState('');
  const [error, setError] = useState<string | null>(null);
  // A grant to more people than one request may name goes in several.
  const [progress, setProgress] = useState<{ reached: number; total: number } | null>(null);
  const active = members.filter((m) => m.status === 'active');
  const page = useMemberPage(active);
  const role = screen.access.roles.find((r) => r.roleId === roleId);
  const isOwner = screen.seat.tier === 'owner';
  const ownerOnly = role?.sensitive && screen.settings.ownerOnlySensitive && !isOwner;
  const pending = grant.isPending || grantMany.isPending;
  const people = chosen.map((p) => active.find((m) => m.principalId === p)).filter(Boolean);

  function toggle(principalId: string, on: boolean) {
    // A sensitive role goes to one person at a time, each with its own proof.
    if (role?.sensitive) setChosen(on ? [principalId] : []);
    else setChosen((prev) => (on ? [...prev, principalId] : prev.filter((p) => p !== principalId)));
  }

  function submit() {
    if (!role || chosen.length === 0) return;
    setError(null);
    // The end of the chosen day, local time.
    const expiresAt = until ? new Date(`${until}T23:59:59`).toISOString() : undefined;
    const done = (title: string) => {
      toast({ title });
      setChosen([]);
      setUntil('');
    };
    if (role.sensitive) {
      onSensitive(
        { principalId: chosen[0], roleId, ...(expiresAt ? { expiresAt } : {}) },
        role.label,
      );
      return;
    }
    if (chosen.length === 1) {
      grant.mutate(
        {
          principalId: chosen[0],
          roleId,
          ...(expiresAt ? { expiresAt } : {}),
          expectedAccessPolicyId: screen.access.policyId,
        },
        {
          onSuccess: () => done(`${role.label} given to ${memberLabel(people[0], chosen[0])}`),
          onError: (err) => setError(describeAppAccessError(err)),
        },
      );
      return;
    }
    const total = chosen.length;
    setProgress(total > APP_ACCESS_BULK_GRANT_MAX ? { reached: 0, total } : null);
    grantMany.mutate(
      {
        principalIds: chosen,
        roleId,
        ...(expiresAt ? { expiresAt } : {}),
        expectedAccessPolicyId: screen.access.policyId,
        onProgress: (reached) => {
          if (total > APP_ACCESS_BULK_GRANT_MAX) setProgress({ reached, total });
        },
      },
      {
        onSuccess: (result) =>
          done(
            `${role.label} given to ${result.assignments.length} ${
              result.assignments.length === 1 ? 'person' : 'people'
            }${result.alreadyAssigned.length ? ` (${result.alreadyAssigned.length} already had it)` : ''}`,
          ),
        onError: (err) => {
          if (err instanceof BulkGrantStoppedError) {
            // What landed stays; the people not reached stay chosen, so
            // giving the role again carries on from them.
            const reached = total - err.notReached.length;
            setChosen(err.notReached);
            setError(
              `It stopped part-way: ${role.label} was given to the first ${reached} of ${total} people chosen` +
                `${err.done.alreadyAssigned.length ? ` (${err.done.alreadyAssigned.length} of them already had it)` : ''}` +
                `; the other ${err.notReached.length} were not given it — ${describeAppAccessError(err.failure)} ` +
                'They are still chosen: give the role again to carry on.',
            );
          } else {
            setError(describeAppAccessError(err));
          }
        },
        onSettled: () => setProgress(null),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Give a role</CardTitle>
        <CardDescription>
          Choose a role, then the people. A sensitive role goes to one person at a time
          {isOwner && !screen.settings.ownerDualControl
            ? ' — as an owner you give it alone, on the record'
            : ' and needs a second person’s approval'}
          . Add an end date for temporary access.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <div className="grid gap-1.5">
            <Label htmlFor="grant-role">Role (from {appName})</Label>
            <select
              id="grant-role"
              className="h-9 rounded-md border border-input bg-background px-2"
              value={roleId}
              onChange={(e) => {
                setRoleId(e.target.value);
                const next = screen.access.roles.find((r) => r.roleId === e.target.value);
                if (next?.sensitive) setChosen((prev) => prev.slice(0, 1));
              }}
            >
              <option value="">Choose…</option>
              {screen.access.roles.map((r) => (
                <option key={r.roleId} value={r.roleId}>
                  {r.label}
                  {r.sensitive ? ' (sensitive)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="grant-until">Until (optional)</Label>
            <Input
              id="grant-until"
              type="date"
              value={until}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
        </div>
        <MemberFilter page={page} id="grant-find" label="Find a person to give the role" />
        <fieldset className="grid max-h-56 gap-1 overflow-y-auto rounded-md border border-border p-2">
          <legend className="px-1 text-xs text-muted-foreground">People</legend>
          {page.rows.map((m) => {
            const eligible = !role || tierMeets(m.role, role.minTier);
            return (
              <label key={m.principalId} className="flex items-center gap-2">
                <Checkbox
                  checked={chosen.includes(m.principalId)}
                  disabled={!eligible || pending}
                  onCheckedChange={(v) => toggle(m.principalId, v === true)}
                  aria-label={memberLabel(m, m.principalId)}
                />
                <span className={eligible ? '' : 'text-muted-foreground'}>
                  {memberLabel(m, m.principalId)}{' '}
                  <span className="text-xs capitalize text-muted-foreground">{m.role}</span>
                  {!eligible && role && (
                    <span className="text-xs"> — needs at least {role.minTier}</span>
                  )}
                </span>
              </label>
            );
          })}
        </fieldset>
        <MemberPager page={page} label="Pages of people to give the role" />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={submit}
            disabled={!role || chosen.length === 0 || pending || !!ownerOnly}
          >
            {role?.sensitive
              ? isOwner && !screen.settings.ownerDualControl
                ? 'Give role…'
                : 'Propose…'
              : chosen.length > 1
                ? `Give role to ${chosen.length} people`
                : 'Give role'}
          </Button>
          {progress && (
            <span className="text-muted-foreground" role="status">
              Giving the role… {progress.reached} of {progress.total} people
            </span>
          )}
        </div>
        {ownerOnly && (
          <p className="text-muted-foreground">
            Only an owner manages sensitive roles in this workspace.
          </p>
        )}
        {error && <p className="text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

/** "1 person" / "12 people". */
function peopleCount(n: number): string {
  return n === 1 ? '1 person' : `${n} people`;
}

/**
 * Who accepting a role's additions reaches: everyone who holds it, by count
 * and — on the manager's screen, which lists assignments — by name.
 */
function holdersLine(count: number, names: string[]): string {
  if (count === 0) return 'Nobody holds this role yet.';
  const shown = names.slice(0, 5);
  const rest = count - shown.length;
  const who =
    shown.length === 0 ? '' : `: ${shown.join(', ')}${rest > 0 ? ` and ${rest} more` : ''}`;
  return `Accepting gives it to the ${peopleCount(count)} who hold this role${who}.`;
}
